import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import api from "@/lib/api";
import { usePageTitle } from "@/lib/usePageTitle";

function sessionNo(date) {
  const m = (date || "").match(/session\s*(\d+)/i);
  return m ? m[1].padStart(2, "0") : null;
}

function SessionRow({ ev, i, past }) {
  const no = sessionNo(ev.date);
  const soon = ev.status === "soon";
  const muted = past || soon;
  return (
    <Reveal delay={i * 0.08}>
      <div
        className="group grid md:grid-cols-12 gap-6 md:gap-8 py-10 md:py-14 items-start"
        data-testid={past ? `dialogue-past-${i}` : soon ? `dialogue-soon-${i}` : `dialogue-upcoming-${i}`}
      >
        <div className="md:col-span-2">
          {no && (
            <span className={`font-serif text-2xl leading-none ${muted ? "text-ink/15" : "text-ink/15 group-hover:text-ember/50 transition-colors duration-500"}`}>
              {no}
            </span>
          )}
          <p className="mt-2 text-[11px] tracking-[0.2em] uppercase text-fog">{ev.date}</p>
        </div>
        <div className="md:col-span-7">
          <div className="flex flex-wrap gap-2 mb-4">
            {!past && (
              soon ? (
                <span className="text-[10px] tracking-[0.15em] uppercase border border-line px-3 py-1 text-fog">Coming soon</span>
              ) : (
                <span className="text-[10px] tracking-[0.15em] uppercase border border-ember text-ember px-3 py-1">Upcoming</span>
              )
            )}
            <span className="text-[10px] tracking-[0.15em] uppercase border border-line px-3 py-1 text-fog">Online</span>
            <span className="text-[10px] tracking-[0.15em] uppercase border border-line px-3 py-1 text-fog">Free</span>
          </div>
          <h3 className={`font-serif text-lg tracking-tight leading-snug ${muted ? "text-ink/70" : ""}`}>
            {ev.title}
          </h3>
          <p className="mt-4 text-sm md:text-base text-fog leading-relaxed max-w-2xl">{ev.description}</p>
        </div>
        <div className="md:col-span-3 flex md:flex-col md:items-end items-start gap-3">
          {!past && !soon && ev.rsvp_link && (
            <a
              href={ev.rsvp_link}
              data-testid={`dialogue-register-${i}`}
              className="group/btn inline-flex items-center gap-2 px-6 py-3 text-sm tracking-wide border border-ink hover:bg-ink hover:text-bone transition-colors duration-300"
            >
              Register
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
            </a>
          )}
          {ev.more_link && (
            ev.more_link.startsWith("/") ? (
              <Link
                to={ev.more_link}
                data-testid={past ? `dialogue-past-link-${i}` : `dialogue-more-${i}`}
                className={`group/btn inline-flex items-center gap-2 px-6 py-3 text-sm tracking-wide border transition-colors duration-300 ${
                  past
                    ? "border-ink text-ink hover:bg-ink hover:text-bone"
                    : soon
                      ? "border-line text-fog hover:border-ink hover:text-ink"
                      : "border-line text-ink/80 hover:border-ink hover:text-ink"
                }`}
              >
                {past ? "Session note" : "Find out more"}
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
              </Link>
            ) : (
              <a
                href={ev.more_link}
                data-testid={past ? `dialogue-past-link-${i}` : `dialogue-more-${i}`}
                className={`group/btn inline-flex items-center gap-2 px-6 py-3 text-sm tracking-wide border transition-colors duration-300 ${
                  past
                    ? "border-ink text-ink hover:bg-ink hover:text-bone"
                    : soon
                      ? "border-line text-fog hover:border-ink hover:text-ink"
                      : "border-line text-ink/80 hover:border-ink hover:text-ink"
                }`}
              >
                {past ? "Session note" : "Find out more"}
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
              </a>
            )
          )}
        </div>
      </div>
    </Reveal>
  );
}

export default function ResearchDialogues() {
  usePageTitle("Research Dialogues | Mentheris", "Monthly one-hour online sessions exploring one Mentheris research idea in depth. Free to attend, open registration.");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = () => {
    setLoading(true);
    setLoadError(false);
    api
      .get("/events")
      .then(({ data }) => setEvents(data.filter((e) => e.kind === "dialogue")))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  const no = (e) => parseInt(sessionNo(e.date) || "0", 10);
  const upcoming = events.filter((e) => e.status !== "past").sort((a, b) => no(a) - no(b));
  const past = events.filter((e) => e.status === "past").sort((a, b) => no(b) - no(a));

  return (
    <main className="pt-24 md:pt-32" data-testid="dialogues-page">
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 pb-20 md:pb-28">
        <h1 className="font-serif font-medium tracking-tighter leading-[0.95] text-5xl" data-testid="dialogues-headline">
          <MaskedLine delay={0.25}>Research <span className="italic text-ember">Dialogues</span>.</MaskedLine>
        </h1>
        <Reveal delay={0.55} className="mt-10 max-w-2xl">
          <p className="text-base md:text-lg text-fog leading-relaxed">
            Structured series of open research sessions exploring the human dimensions of
            technological transformation. Each series develops a distinct conceptual direction,
            bringing together researchers, practitioners, and thinkers to investigate problems
            that matter.
          </p>
        </Reveal>
      </section>

      <section data-testid="current-series-section" className="bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-12 md:py-16">
          <Reveal>
            <p className="text-xs tracking-[0.25em] uppercase text-fog">Current series</p>
          </Reveal>
          <Reveal delay={0.1} className="mt-6">
            <h2 className="font-serif text-[38px] tracking-tight">Technology-Human Disequilibrium</h2>
            <p className="mt-6 text-fog leading-relaxed max-w-2xl">
              A series examining what happens when technological expansion outpaces
              human adaptive capacity, and what it means for individuals, organizations, and
              society. Open to researchers and practitioners.
            </p>
            <p className="mt-4 text-sm text-fog leading-relaxed max-w-2xl">
              This series builds on the conceptual paper{" "}
              <a
                href="https://ssrn.com/abstract=6426239"
                data-testid="series-paper-link"
                className="link-editorial text-ink"
              >
                Technology-Human Disequilibrium: A conceptual paper for human-centered technological systems
              </a>
              , Puica, E. (2026).
            </p>
            <Link
              to="/research"
              data-testid="series-publications-link"
              className="group/pub inline-flex items-center gap-1.5 mt-6 text-xs tracking-[0.2em] uppercase text-fog hover:text-ink transition-colors duration-300"
            >
              See Publications
              <ArrowUpRight size={13} className="transition-transform duration-300 group-hover/pub:translate-x-0.5 group-hover/pub:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section data-testid="dialogues-upcoming-section" className="bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-12 md:py-16">
          <Reveal>
            <div className="flex items-end justify-between mb-6">
              <h2 className="font-serif text-4xl tracking-tight">Upcoming</h2>
              <p className="hidden md:block text-xs tracking-[0.25em] uppercase text-fog">Open for registration</p>
            </div>
          </Reveal>
          {loadError ? (
            <div className="py-10" data-testid="dialogues-load-error">
              <p className="text-fog text-sm">
                The sessions could not be loaded. Please try again.
              </p>
              <button
                onClick={load}
                data-testid="dialogues-retry-btn"
                className="mt-4 text-xs tracking-[0.2em] uppercase text-ink underline underline-offset-4 hover:text-ember transition-colors duration-300"
              >
                Retry
              </button>
            </div>
          ) : loading ? (
            <p className="text-fog text-sm py-10" data-testid="dialogues-loading">
              Loading sessions…
            </p>
          ) : upcoming.length === 0 ? (
            <p className="text-fog text-sm py-10" data-testid="dialogues-upcoming-empty">
              New sessions are announced soon.
            </p>
          ) : (
            <div>
              {upcoming.map((ev, i) => (
                <SessionRow key={ev.id} ev={ev} i={i} past={false} />
              ))}
            </div>
          )}
        </div>
      </section>

      {past.length > 0 && (
        <section data-testid="dialogues-past-section" className="bg-white">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-28">
            <Reveal>
              <div className="flex items-end justify-between mb-6">
                <h2 className="font-serif text-4xl tracking-tight text-ink/60">Previous sessions</h2>
                <p className="hidden md:block text-xs tracking-[0.25em] uppercase text-fog">Archive</p>
              </div>
            </Reveal>
            <div>
              {past.map((ev, i) => (
                <SessionRow key={ev.id} ev={ev} i={i} past={true} />
              ))}
            </div>
          </div>
        </section>
      )}

      <section>
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-14 md:py-16">
          <Reveal>
            <p className="text-xs tracking-[0.25em] uppercase text-fog">Contribute</p>
          </Reveal>
          <Reveal delay={0.1} className="mt-8">
            <p className="font-serif text-2xl md:text-4xl leading-[1.25] tracking-tight max-w-3xl">
              More series in development. Interested in contributing to a future Research
              Dialogue?
            </p>
            <a
              href="mailto:contact@mentheris.com"
              data-testid="dialogues-contribute-link"
              className="group inline-flex items-center gap-3 bg-ink text-bone px-8 py-4 text-sm tracking-wide hover:bg-ember transition-colors duration-300 mt-8"
            >
              Get in touch
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </Reveal>
        </div>
      </section>

    </main>
  );
}
